import { useState, useCallback, useRef, useEffect } from 'react';

const MAX_CACHED_IMAGES = 20; // LRU 캐시 최대 개수

/**
 * 이미지 크롭 관련 상태 및 로직을 관리하는 커스텀 훅
 * - 크롭 모달 표시/숨김
 * - 크롭 영역 설정
 * - 크롭된 이미지 저장 (Blob URL + LRU 캐시)
 */
const useCropImage = () => {
    const [showCropModal, setShowCropModal] = useState(false);
    const [cropFlashId, setCropFlashId] = useState(null);
    const [crop, setCrop] = useState({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImages, setCroppedImages] = useState({});

    const cropImageRef = useRef(null);
    const blobUrlsRef = useRef({}); // Blob URL 추적용
    const accessOrderRef = useRef([]); // LRU 순서 추적

    // 크롭 모달 열기
    const openCropModal = useCallback((flashId) => {
        setCropFlashId(flashId);
        setCrop({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
        setCompletedCrop(null);
        setShowCropModal(true);
    }, []);

    // 크롭 모달 닫기
    const closeCropModal = useCallback(() => {
        setShowCropModal(false);
        setCropFlashId(null);
        setCompletedCrop(null);
    }, []);

    // Blob URL 해제 헬퍼
    const revokeBlobUrl = useCallback((flashId) => {
        if (blobUrlsRef.current[flashId]?.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrlsRef.current[flashId]);
            delete blobUrlsRef.current[flashId];
        }
    }, []);

    // LRU 캐시 정리 (가장 오래된 항목 제거)
    const enforceLRULimit = useCallback(() => {
        while (accessOrderRef.current.length > MAX_CACHED_IMAGES) {
            const oldestId = accessOrderRef.current.shift();
            revokeBlobUrl(oldestId);
            setCroppedImages(prev => {
                const newState = { ...prev };
                delete newState[oldestId];
                return newState;
            });
        }
    }, [revokeBlobUrl]);

    // LRU 순서 업데이트
    const updateAccessOrder = useCallback((flashId) => {
        accessOrderRef.current = accessOrderRef.current.filter(id => id !== flashId);
        accessOrderRef.current.push(flashId);
    }, []);

    // 크롭 완료 후 이미지 저장 (Blob URL 사용)
    const saveCroppedImage = useCallback((flashId, blobUrl) => {
        // 이전 Blob URL 해제
        revokeBlobUrl(flashId);

        // 새 Blob URL 저장
        blobUrlsRef.current[flashId] = blobUrl;
        updateAccessOrder(flashId);

        setCroppedImages(prev => ({
            ...prev,
            [flashId]: blobUrl
        }));

        // LRU 제한 적용
        enforceLRULimit();

        closeCropModal();
    }, [closeCropModal, revokeBlobUrl, updateAccessOrder, enforceLRULimit]);

    // 크롭 이미지 제거
    const removeCroppedImage = useCallback((flashId) => {
        revokeBlobUrl(flashId);
        accessOrderRef.current = accessOrderRef.current.filter(id => id !== flashId);
        setCroppedImages(prev => {
            const newState = { ...prev };
            delete newState[flashId];
            return newState;
        });
    }, [revokeBlobUrl]);

    // 크롭된 이미지 가져오기 (없으면 원본 URL 반환)
    const getCroppedImageUrl = useCallback((flashId, originalUrl) => {
        return croppedImages[flashId] || originalUrl;
    }, [croppedImages]);

    // 크롭 적용 (캔버스에서 이미지 추출 - Blob URL 사용)
    const applyCrop = useCallback(() => {
        if (!completedCrop || !cropImageRef.current || !cropFlashId) {
            return null;
        }

        const image = cropImageRef.current;
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = completedCrop.width * scaleX;
        canvas.height = completedCrop.height * scaleY;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(
            image,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );

        // Blob URL로 변환 (메모리 효율적)
        canvas.toBlob((blob) => {
            if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                saveCroppedImage(cropFlashId, blobUrl);
            }
        }, 'image/png');

        return true; // 비동기로 처리됨
    }, [completedCrop, cropFlashId, saveCroppedImage]);

    // 모든 Blob URL 정리
    const cleanupAllBlobUrls = useCallback(() => {
        Object.keys(blobUrlsRef.current).forEach(flashId => {
            revokeBlobUrl(flashId);
        });
        accessOrderRef.current = [];
    }, [revokeBlobUrl]);

    // 컴포넌트 언마운트 시 정리
    useEffect(() => {
        const blobUrls = blobUrlsRef.current;

        return () => {
            Object.values(blobUrls).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    return {
        // 상태
        showCropModal,
        cropFlashId,
        crop,
        completedCrop,
        croppedImages,
        cropImageRef,

        // setter
        setShowCropModal,
        setCropFlashId,
        setCrop,
        setCompletedCrop,
        setCroppedImages,

        // 액션
        openCropModal,
        closeCropModal,
        saveCroppedImage,
        removeCroppedImage,
        getCroppedImageUrl,
        applyCrop,
        cleanupAllBlobUrls,
    };
};

export default useCropImage;
